import { client } from '../setup';
import {
  QueryCreator, CriteriaCreator, SingleRecordMutationCreator,
} from './base';
import { createAcceptedProperties, dataRegex } from '../helpers';
import appliancesData from '../mocks/appliances.json';
import * as is from 'is';
import {
  deleteSource, getSources, SourcesQuery,
} from './sources';
import { deleteStream, getStreams } from './streams';
import { getLogger } from '@utils/general';
const logger = getLogger('src.utils.graphQL.creators.appliances');

const acceptedReturnProperties = createAcceptedProperties(
  'id',
  'name',
  'management_address',
  'hostname',
  'public_address',
  'location',
  'model',
  'version',
  'total_connection_count',
  'stream_count',
  'offline_source.name',
  'offline_source.id',
  'offline_source.description',
  'offline_source.sdp_input',
  'offline_source.port',
  'offline_source.path',
  'offline_source.scheme',

  'offline_source.appliance.id',
  'offline_source.device.id',
  'offline_source.appliance.name',
  'offline_source.device.name'
);

const acceptedCriteriaProperties = {
  name: { type: 'string' },
  management_address: { type: 'string' },
  hostname: { type: 'string' },
  public_address: { type: 'string' },
  offline_source_id: { type: 'string' },
  offline_source_name: { type: 'string' },
  location: { type: 'string' },
  model: { type: 'string' },
  version: { type: 'string' },
  or: { type: 'or' },
  and: { type: 'and' },
  not: { type: 'not' },
};

export class SingleApplianceMutation extends SingleRecordMutationCreator {
  constructor(queryType = 'create_appliance', criteria = {}) {
    super(queryType, AppliancesCriteria);
    this.setCriteria(criteria);
  }
  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }
}

class AppliancesCriteria extends CriteriaCreator {
  constructor(criteria = {}) {
    super(criteria);
  }
  get acceptedCriteria() {
    return acceptedCriteriaProperties;
  }
}

export class AppliancesQuery extends QueryCreator {
  #sourceQuery = null;

  constructor(queryType = 'appliances', options = {}) {
    super(queryType, AppliancesCriteria);

    this.#sourceQuery = new SourcesQuery('sources', {
      includeApplianceQuery: !!options?.includeApplianceQuery,
      includeStreamQuery: false,
      includeDeviceQuery: !!options?.includeDeviceQuery,
    });

    if (options?.pagination) {
      const { count_per_page, start_on_page } = options.pagination;
      if (count_per_page) this.addPagination('countPerPage', count_per_page);
      if (start_on_page) this.addPagination('startOnPage', start_on_page);
      this.addPaginationReturn('total_pages', 'total_records', 'count_per_page', 'current_page');
    }

    if (options?.sort) {
      this.setSort(options.sort.field, options.sort.direction);
    }
  }

  get acceptedReturnProperties() {
    return acceptedReturnProperties;
  }

  addReturnProperties(...properties) {
    for (const property of properties) {
      if (property.includes('source')) {
        const [, ...sourceProperty] = property.split('.');
        this.#sourceQuery.addReturnProperties(sourceProperty.join('.'));
        continue;
      }

      super.addReturnProperties(property);
    }
    return this;
  }

  returnSyntax() {
    const syntax = super.returnSyntax();
    if (syntax === '') {
      return syntax;
    }

    const [, data] = syntax.match(dataRegex);
    const sourceSyntax = this.#sourceQuery.returnSyntax();

    const dataSyntax = [data.trim()];
    if (!is.empty(sourceSyntax)) {
      dataSyntax.push(
        sourceSyntax.replace(/^data/, 'offline_source').replace(/\n$/, '').trim()
      );
    }
    return `data {\n${dataSyntax.join('\n')}\n}`;
  }
}

export function getAppliances(id, criteria = {}, options = {}) {
  let query;
  if (id) {
    query = new AppliancesQuery('appliance', options);
    query.setId(id);
  } else {
    query = new AppliancesQuery('appliances', options);

    if (!is.empty(criteria)) {
      Object.keys(criteria).forEach((key) => {
        query.addCriteria(key, criteria[key]);
      });
    }
  }
  return {
    query,
    execute() {
      return /^dev(?:elop(?:ment)?)?$/i.test(import.meta.env.VITE_ENV)
        ? Promise.resolve().then(() => {
          const appliances = id
            ? appliancesData.find((appliance) => appliance.id === id)
            : criteria
              ? appliancesData.filter((appliance) => {
                return Object.keys(criteria).every((key) => appliance[key] === criteria[key]);
              })
              : appliancesData;
          return { data: appliances };
        })
        : client.query(query.name, query.toString());
    },
  };
}

export function updateAppliance(mutationType, id, input) {
  let mutation;
  mutation = new SingleApplianceMutation(mutationType, input);
  mutation.setId(id);

  return {
    mutation,
    execute() {
      return client.mutate(mutation.name, mutation.toString());
    },
  };
}

export const deleteAppliance = async (appliance) => {
  const { execute: executeApplianceRemoval } = updateAppliance('remove_appliance', appliance.id);
  const { execute: executeGetSources, query: sourceQuery } = getSources(null, { applianceId: appliance.id });
  sourceQuery.addReturnProperties('id', 'name');

  const { execute: executeGetStreams, query: streamQuery } = getStreams(null, { applianceId: appliance.id });
  streamQuery.addReturnProperties('id', 'name');

  try {
    const { error: getSourcesError, data: sourcesAssociatedWithAppliance } = await executeGetSources();
    const { error: getStreamsError, data: streamsAssociatedWithAppliance } = await executeGetStreams();

    if (getSourcesError) {
      throw new Error(getSourcesError);
    }

    if (getStreamsError) {
      throw new Error(getStreamsError);
    }

    if (streamsAssociatedWithAppliance) {
      for (const stream of streamsAssociatedWithAppliance) {
        await deleteStream(stream);
      }
    }

    if (sourcesAssociatedWithAppliance) {
      for (const source of sourcesAssociatedWithAppliance) {
        await deleteSource(source);
      }
    }

    await executeApplianceRemoval();
  }
  catch (error) {
    logger.error(error, `Failed to delete appliance ${appliance.id}.`);
    throw new Error(error);
  }
};
